import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import {
  Users,
  Calendar,
  FileText,
  Award,
  Euro,
  BarChart3,
  Shield,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
  Play,
  Linkedin,
  Twitter,
  Globe,
  Lock,
  TrendingUp,
  UserCheck,
  FileCheck,
  Bell,
  Smartphone,
  Cloud,
  RefreshCw,
  X,
  Menu,
  Sparkles
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(null);
  const [visibleSections, setVisibleSections] = useState({});
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const sectionRefs = useRef({});

  // Fetch packages from API
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/packages`);
        if (response.ok) {
          const data = await response.json();
          setPackages(data);
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
      } finally {
        setPackagesLoading(false);
      }
    };
    fetchPackages();
  }, []);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const features = [
    {
      icon: Users,
      title: "Gestion des Employés",
      description: "Dossiers complets, contrats, documents administratifs. Tout est centralisé et accessible en un clic.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Calendar,
      title: "Congés & Absences",
      description: "Workflow de validation intelligent, calendrier partagé, calcul automatique des soldes.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Award,
      title: "Habilitations",
      description: "Suivi des certifications avec alertes automatiques. Ne manquez plus aucune échéance.",
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: Euro,
      title: "Préparation Paie",
      description: "Variables de paie, primes, heures sup. Export direct vers SILAE et autres logiciels.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: FileText,
      title: "Documents RH",
      description: "Génération automatique d'attestations et contrats à partir de modèles personnalisables.",
      color: "from-red-500 to-rose-500"
    },
    {
      icon: BarChart3,
      title: "Analytics RH",
      description: "Tableaux de bord interactifs : turnover, pyramide des âges, indicateurs clés.",
      color: "from-indigo-500 to-violet-500"
    }
  ];

  const detailedFeatures = [
    {
      icon: UserCheck,
      title: "Prise en main immédiate",
      description: "Interface intuitive, aucune formation requise pour démarrer"
    },
    {
      icon: Bell,
      title: "Alertes automatiques",
      description: "Expiration d'habilitations, fin de période d'essai, anniversaires"
    },
    {
      icon: FileCheck,
      title: "100% conforme RGPD",
      description: "Données hébergées en France, politique de confidentialité stricte"
    },
    {
      icon: Smartphone,
      title: "Accessible partout",
      description: "Interface responsive, accédez à vos données sur tous vos appareils"
    },
    {
      icon: Cloud,
      title: "Sauvegarde automatique",
      description: "Vos données sont sécurisées et sauvegardées quotidiennement"
    },
    {
      icon: RefreshCw,
      title: "Évolutions continues",
      description: "Nouvelles fonctionnalités régulières incluses dans votre abonnement"
    }
  ];


  // All available modules for feature display
  const allModules = [
    'employees', 'habilitations', 'conges', 'payroll',
    'documents', 'entretiens', 'expenses', 'rse',
    'ai_chat', 'calendar', 'admin', 'dashboard'
  ];


  const faqs = [
    {
      question: "À qui s'adresse Nexus SIRH ?",
      answer: "Nexus SIRH est conçu pour les TPE et PME françaises de tous secteurs d'activité. Que vous ayez 5 ou 500 salariés, notre solution s'adapte à votre taille et vos besoins spécifiques."
    },
    {
      question: "Comment fonctionne la période d'essai gratuite ?",
      answer: "Vous bénéficiez de 7 jours d'essai gratuit avec accès complet à toutes les fonctionnalités. Aucune carte bancaire n'est requise pour démarrer. À la fin de l'essai, choisissez simplement le forfait adapté à vos besoins."
    },
    {
      question: "Mes données sont-elles sécurisées et conformes RGPD ?",
      answer: "Absolument. Vos données sont hébergées en France sur des serveurs sécurisés. Nous appliquons les plus hauts standards de sécurité : chiffrement SSL, sauvegardes quotidiennes automatiques, et conformité totale avec le RGPD. Vous restez propriétaire de vos données."
    },
    {
      question: "Puis-je connecter Nexus SIRH à mon logiciel de paie ?",
      answer: "Oui, Nexus SIRH s'intègre nativement avec SILAE et propose des exports compatibles avec la plupart des logiciels de paie du marché. L'export des variables de paie se fait en quelques clics."
    },
    {
      question: "Comment gérer les congés et absences de mes salariés ?",
      answer: "Nexus SIRH propose un workflow complet : vos salariés déposent leurs demandes depuis leur espace personnel, les managers valident, puis les RH finalisent. Tout est tracé, avec alertes automatiques et calcul des soldes en temps réel."
    },
    {
      question: "Comment fonctionne le suivi des habilitations et formations ?",
      answer: "Vous définissez les habilitations requises par poste (CACES, permis, SST...), le système vous alerte automatiquement avant expiration. Idéal pour les secteurs réglementés comme le BTP, le transport ou l'industrie."
    },
    {
      question: "Puis-je importer mes données existantes ?",
      answer: "Oui, nous proposons un import facile depuis Excel ou CSV. Vous pouvez importer votre base salariés, les soldes de congés, les habilitations en quelques minutes."
    },
    {
      question: "Y a-t-il un engagement de durée ?",
      answer: "Non, nos abonnements sont sans engagement. Vous pouvez résilier à tout moment depuis votre espace client. La facturation s'arrête à la fin de la période en cours."
    }
  ];


  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                isScrolled ? 'bg-[#0F5156]' : 'bg-white/20 backdrop-blur'
              }`}>
                <Users className={`w-6 h-6 ${isScrolled ? 'text-white' : 'text-white'}`} />
              </div>
              <span className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-slate-900' : 'text-white'
              }`}>
                Nexus SIRH
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {[
                { label: 'Fonctionnalités', id: 'features' },
                { label: 'Tarifs', id: 'pricing' },
                { label: 'FAQ', id: 'faq' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`font-medium transition-colors ${
                    isScrolled ? 'text-slate-600 hover:text-[#0F5156]' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className={`font-medium ${
                  isScrolled ? 'text-slate-600 hover:text-slate-900' : 'text-white hover:bg-white/10'
                }`}
              >
                Se connecter
              </Button>
              <Button
                onClick={() => navigate('/login?tab=register')}
                className={`font-semibold ${
                  isScrolled
                    ? 'bg-[#0F5156] hover:bg-[#0d464a] text-white'
                    : 'bg-white text-[#0F5156] hover:bg-white/90'
                }`}
              >
                Démarrer gratuitement
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="px-4 py-4 space-y-4">
              {['Fonctionnalités', 'Tarifs', 'FAQ'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="block w-full text-left py-2 text-slate-600 hover:text-[#0F5156]"
                >
                  {item}
                </button>
              ))}
              <hr />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
              >
                Se connecter
              </Button>
              <Button
                className="w-full bg-[#0F5156] hover:bg-[#0d464a]"
                onClick={() => { navigate('/login?tab=register'); setMobileMenuOpen(false); }}
              >
                Démarrer gratuitement
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center gradient-mesh overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#C27868]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in">
                <Sparkles className="w-4 h-4" />
                Solution 100% française pour TPE/PME
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-slide-up">
                La gestion RH
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-white">
                  enfin simplifiée
                </span>
              </h1>

              <p className="text-xl text-white/80 mb-8 max-w-xl animate-slide-up delay-100">
                Centralisez employés, congés, habilitations et paie dans une plateforme
                intuitive conçue pour les TPE et PME françaises.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-slide-up delay-200">
                <Button
                  size="lg"
                  onClick={() => navigate('/login?tab=register')}
                  className="bg-white text-[#0F5156] hover:bg-white/90 text-lg px-8 py-6 font-semibold shadow-xl hover-lift"
                >
                  Essai gratuit 7 jours
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('demo')}
                  className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 backdrop-blur-sm"
                >
                  <Play className="mr-2 w-5 h-5" />
                  Voir la démo
                </Button>
              </div>

              <div className="mt-10 flex items-center gap-6 justify-center lg:justify-start animate-fade-in delay-300">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-white/80" />
                  <span className="text-white/80 text-sm">Données hébergées en France</span>
                </div>
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-white/80" />
                  <span className="text-white/80 text-sm">Conforme RGPD</span>
                </div>
              </div>
            </div>

            {/* Right content - Dashboard preview */}
            <div className="hidden lg:block relative animate-slide-right delay-300">
              <div className="relative">
                {/* Browser mockup */}
                <div className="bg-slate-800 rounded-xl shadow-2xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-slate-900">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-slate-700 rounded-md px-3 py-1 text-xs text-slate-400 text-center">
                        nexus-sirh.fr/dashboard
                      </div>
                    </div>
                  </div>
                  <div className="bg-slate-100 p-4">
                    {/* Mini dashboard preview */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-[#0F5156] flex items-center justify-center">
                            <Users className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold text-slate-900">Tableau de bord</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {[
                          { label: 'Employés', value: '47', color: 'bg-blue-500' },
                          { label: 'En congés', value: '3', color: 'bg-amber-500' },
                          { label: 'À renouveler', value: '5', color: 'bg-red-500' }
                        ].map((stat, i) => (
                          <div key={i} className="bg-slate-50 rounded-lg p-3">
                            <div className={`w-2 h-2 rounded-full ${stat.color} mb-2`} />
                            <div className="text-xl font-bold text-slate-900">{stat.value}</div>
                            <div className="text-xs text-slate-500">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                      <div className="h-24 bg-gradient-to-r from-[#0F5156]/10 to-[#C27868]/10 rounded-lg flex items-end p-2 gap-1">
                        {[40, 65, 45, 80, 55, 70, 60].map((h, i) => (
                          <div key={i} className="flex-1 bg-[#0F5156] rounded-t" style={{ height: `${h}%` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating cards */}
                <div className="absolute -left-10 top-1/4 bg-white rounded-lg shadow-xl p-4 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">Congé validé</div>
                      <div className="text-xs text-slate-500">Marie D. - 3 jours</div>
                    </div>
                  </div>
                </div>

                <div className="absolute -right-5 bottom-1/4 bg-white rounded-lg shadow-xl p-4 animate-float delay-500">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Bell className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">Alerte habilitation</div>
                      <div className="text-xs text-slate-500">Expire dans 15 jours</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>


      {/* Features Section */}
      <section
        id="features"
        ref={(el) => (sectionRefs.current['features'] = el)}
        className="py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-[#0F5156]/10 text-[#0F5156] rounded-full text-sm font-medium mb-4">
              Fonctionnalités
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Tout pour gérer vos RH
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Une suite complète d'outils pensés pour simplifier votre quotidien
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`feature-card hover-lift ${visibleSections['features'] ? 'animate-slide-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 shadow-lg`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Features Grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-[#0F5156]/10 text-[#0F5156] rounded-full text-sm font-medium mb-4">
                Avantages
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Pourquoi les entreprises choisissent Nexus SIRH
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Nous avons conçu notre plateforme en écoutant les besoins réels des responsables RH
                et dirigeants de TPE/PME.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {detailedFeatures.map((feature, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0F5156]/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-[#0F5156]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{feature.title}</h4>
                      <p className="text-sm text-slate-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#0F5156] flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Gain de temps moyen</h4>
                    <p className="text-slate-500">Constaté par nos clients</p>
                  </div>
                </div>
                <div className="text-6xl font-bold text-[#0F5156] mb-2">60%</div>
                <p className="text-slate-600">de temps gagné sur les tâches administratives RH</p>

                <div className="mt-8 pt-8 border-t border-slate-200">
                  <div className="flex items-center gap-4">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <p className="text-sm text-slate-600">
                      Conçu pour les <span className="font-semibold">TPE et PME françaises</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -z-10 top-10 -right-10 w-40 h-40 bg-[#0F5156]/10 rounded-full blur-3xl" />
              <div className="absolute -z-10 bottom-10 -left-10 w-40 h-40 bg-[#C27868]/10 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-24 bg-[#0F5156]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Découvrez Nexus SIRH en action
            </h2>
            <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
              Regardez comment notre plateforme peut transformer votre gestion RH
            </p>

            <div className="relative max-w-4xl mx-auto">
              <div className="aspect-video bg-slate-800 rounded-2xl overflow-hidden shadow-2xl flex items-center justify-center">
                <button className="w-20 h-20 rounded-full bg-white flex items-center justify-center hover-scale">
                  <Play className="w-8 h-8 text-[#0F5156] ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        ref={(el) => (sectionRefs.current['pricing'] = el)}
        className="py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-[#0F5156]/10 text-[#0F5156] rounded-full text-sm font-medium mb-4">
              Tarifs
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Des tarifs adaptés à votre taille
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
              Pas de surprises, pas de frais cachés. Changez de formule à tout moment.
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-4 bg-slate-100 p-1 rounded-full">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600'
                }`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  billingPeriod === 'annual'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600'
                }`}
              >
                Annuel
                {packages.length > 0 && packages[0]?.annual_savings_percent > 0 && (
                  <span className="ml-2 text-xs text-green-600 font-semibold">
                    -{Math.round(packages[0].annual_savings_percent)}%
                  </span>
                )}
              </button>
            </div>
          </div>

          {packagesLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#0F5156]" />
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-500">Aucun forfait disponible pour le moment.</p>
              <Button
                className="mt-4 bg-[#0F5156] hover:bg-[#0d464a]"
                onClick={() => navigate('/login?tab=register')}
              >
                Contactez-nous
              </Button>
            </div>
          ) : (
            <div className={`grid gap-8 max-w-6xl mx-auto ${
              packages.length === 1 ? 'md:grid-cols-1 max-w-md' :
              packages.length === 2 ? 'md:grid-cols-2 max-w-3xl' :
              'md:grid-cols-3'
            }`}>
              {packages.map((pkg, index) => {
                const isPopular = index === 1 || pkg.code === 'business';
                const monthlyPrice = Math.round(pkg.price_monthly_ht);
                const annualMonthlyPrice = Math.round(pkg.price_annual_ht / 12);
                const pkgModules = pkg.modules || [];

                return (
                  <Card
                    key={pkg.id || index}
                    className={`pricing-card relative border-2 transition-all duration-300 hover-lift ${
                      isPopular
                        ? 'popular border-[#0F5156]'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {isPopular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span className="bg-[#0F5156] text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                          Le plus populaire
                        </span>
                      </div>
                    )}

                    <CardContent className="p-8">
                      <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-slate-900 mb-2">{pkg.name}</h3>
                        <p className="text-slate-500">{pkg.description || 'Forfait adapté à vos besoins'}</p>
                        <div className="mt-6">
                          <span className="text-5xl font-bold text-slate-900">
                            {billingPeriod === 'monthly' ? monthlyPrice : annualMonthlyPrice}€
                          </span>
                          <span className="text-slate-500">/mois HT</span>
                        </div>
                        {billingPeriod === 'annual' && pkg.annual_savings_percent > 0 && (
                          <p className="text-sm text-green-600 mt-1">
                            -{Math.round(pkg.annual_savings_percent)}% par rapport au mensuel
                          </p>
                        )}
                        <p className="text-sm text-slate-500 mt-2">
                          {pkg.max_employees === -1 || pkg.max_employees === null
                            ? 'Employés illimités'
                            : `Jusqu'à ${pkg.max_employees} employés`}
                        </p>
                      </div>

                      <ul className="space-y-4 mb-8">
                        {allModules.map((moduleKey) => {
                          const isIncluded = pkgModules.includes(moduleKey);
                          const moduleLabel = pkg.modules_labels?.[moduleKey] || moduleKey;
                          return (
                            <li key={moduleKey} className="flex items-center gap-3">
                              {isIncluded ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                              ) : (
                                <X className="w-5 h-5 text-slate-300 flex-shrink-0" />
                              )}
                              <span className={isIncluded ? 'text-slate-700' : 'text-slate-400'}>
                                {moduleLabel}
                              </span>
                            </li>
                          );
                        })}
                      </ul>

                      <Button
                        className={`w-full py-6 font-semibold ${
                          isPopular
                            ? 'bg-[#0F5156] hover:bg-[#0d464a] text-white'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                        }`}
                        onClick={() => navigate('/login?tab=register')}
                      >
                        Essai gratuit
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <p className="text-center mt-8 text-slate-500">
            Tous les prix sont HT. TVA applicable. 7 jours d'essai gratuit inclus.
          </p>
        </div>
      </section>


      {/* FAQ Section */}
      <section
        id="faq"
        ref={(el) => (sectionRefs.current['faq'] = el)}
        className="py-24 bg-white"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-[#0F5156]/10 text-[#0F5156] rounded-full text-sm font-medium mb-4">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Questions fréquentes
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-semibold text-slate-900 pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${
                    openFaq === index ? 'rotate-180' : ''
                  }`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${
                  openFaq === index ? 'max-h-96' : 'max-h-0'
                }`}>
                  <p className="px-6 pb-6 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 gradient-mesh relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Prêt à transformer votre gestion RH ?
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Simplifiez votre quotidien et gagnez du temps sur vos tâches administratives
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/login?tab=register')}
              className="bg-white text-[#0F5156] hover:bg-white/90 text-lg px-10 py-7 font-semibold shadow-xl hover-lift"
            >
              Démarrer l'essai gratuit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-white/30 text-white hover:bg-white/10 text-lg px-10 py-7"
            >
              Se connecter
            </Button>
          </div>
          <p className="mt-6 text-white/60 text-sm">
            7 jours d'essai gratuit - Aucune carte bancaire requise
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
            {/* Company info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#0F5156] flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white">Nexus SIRH</span>
              </div>
              <p className="mb-6 max-w-sm">
                La solution RH complète pour les TPE et PME françaises.
                Simplifiez votre gestion, gagnez du temps.
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Produit</h4>
              <ul className="space-y-3">
                <li><a href="#features" className="hover:text-white transition-colors">Fonctionnalités</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Tarifs</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Intégrations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Changelog</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Entreprise</h4>
              <ul className="space-y-3">
                <li><a href="#" className="hover:text-white transition-colors">À propos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Carrières</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Légal</h4>
              <ul className="space-y-3">
                <li><a href="#" className="hover:text-white transition-colors">CGU</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Confidentialité</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Mentions légales</a></li>
                <li><a href="#" className="hover:text-white transition-colors">RGPD</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">
              © 2026 Nexus SIRH. Tous droits réservés.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <span className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Français
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Hébergé en France
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
